import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Box } from '@frontierag/frontier-ui-library';
import {
  Delete as DeleteIcon,
  ShoppingBasketOutlined as ShoppingBasketOutlinedIcon,
} from '@frontierag/frontier-ui-library/icons';
import { MAX_CONTENT_WIDTH } from 'appConstants';
import BasketProductCard from 'components/BasketProductCard';
import { useConfirmDialog } from 'contexts/ConfirmDialog';
import { useField } from 'formik';
import EmptyState from 'shared/EmptyState';
import type { ICropFormikProduct } from 'types/product';
import { getProductTitle } from 'utils';

import useCardFields from './useCardFields';

export default function ViewBasket() {
  const [{ value: products = [] }, , { setValue: setProducts }] = useField('products');
  const confirmDialog = useConfirmDialog();
  const { t } = useTranslation('CreateCropOrder', { keyPrefix: 'viewBasket' });

  const onQuantityChange = useCallback(
    async (quantity: number, productId: number) => {
      await setProducts(
        products.map((product, key) =>
          key === productId ? { ...product, packagedItemQty: quantity } : product,
        ),
      );
    },
    [products, setProducts],
  );

  const { getFields } = useCardFields(onQuantityChange);

  const handleDeleteProduct = useCallback(
    async (productId: number | string) => {
      const { confirmed } = await confirmDialog({
        title: t('deleteDialog.title'),
        message: t('deleteDialog.message'),
        onActionLabel: t('deleteDialog.actionBtn'),
      });

      if (!confirmed) return;

      const remainingProducts = [...products];
      remainingProducts.splice(Number(productId), 1);
      await setProducts(remainingProducts);
    },
    [products, setProducts, t, confirmDialog],
  );

  const deleteButtonAction = useMemo(
    () => ({
      title: t('card.deleteBtn'),
      onActionClick: handleDeleteProduct,
      icon: <DeleteIcon fontSize="small" />,
    }),
    [handleDeleteProduct, t],
  );

  return (
    <Box display="flex" data-testid="view-basket-tab">
      {products && products.length > 0 ? (
        <>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              width: MAX_CONTENT_WIDTH,
              minWidth: MAX_CONTENT_WIDTH,
            }}
          >
            {products.map((product: ICropFormikProduct, key: number) => (
              <BasketProductCard
                key={key}
                productId={key}
                fields={getFields(product, key)}
                title={getProductTitle(product)}
                actions={[deleteButtonAction]}
              />
            ))}
          </Box>
        </>
      ) : (
        <EmptyState title={t('card.noProducts')} icon={<ShoppingBasketOutlinedIcon />} />
      )}
    </Box>
  );
}
